import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

// Text-holding slots, keyed by their own node type.
const SLOT_PLACEHOLDERS: Record<string, string> = {
	cardTitle: "Card title",
	tileTitle: "Tile title",
	tileDescription: "Tile description",
	detailsSummary: "Accordion title",
	treeFolderName: "Folder name",
	treeFile: "File name",
	updateLabel: "Label",
	updateDescription: "Date or subtitle",
	frameCaption: "Caption",
	promptDescription: "Description",
	promptText: "Prompt text",
}

// Block containers whose only paragraph is still empty, keyed by the container.
const CONTAINER_PLACEHOLDERS: Record<string, string> = {
	card: "Card text",
	tilePreview: "Image",
	updateContent: "What changed",
	frameMedia: "Image or video",
	paramField: "Description",
	responseField: "Description",
}

/** The hint an empty Mintlify slot shows in the editor, or null for any other node. */
export function slotPlaceholder(
	node: ProseMirrorNode,
	parent: ProseMirrorNode | null,
): string | null {
	if (node.content.size > 0) return null
	const own = SLOT_PLACEHOLDERS[node.type.name]
	if (own) return own
	if (node.type.name !== "paragraph" || !parent) return null
	const hint = CONTAINER_PLACEHOLDERS[parent.type.name]
	if (!hint) return null
	// A card's text starts after its title; any other container holds just this paragraph.
	const blocks =
		parent.type.name === "card" ? parent.childCount - 1 : parent.childCount
	return blocks === 1 ? hint : null
}

const key = new PluginKey("mintlifySlotPlaceholders")

/** Grey hints in empty card, tile, accordion, tree, update, frame, prompt and field slots. */
export const MintlifySlotPlaceholders = Extension.create({
	name: "mintlifySlotPlaceholders",

	addProseMirrorPlugins() {
		const editor = this.editor
		return [
			new Plugin({
				key,
				props: {
					decorations: (state) => {
						if (!editor.isEditable) return null
						const decorations: Decoration[] = []
						state.doc.descendants((node, pos, parent) => {
							const hint = slotPlaceholder(node, parent)
							if (hint) {
								decorations.push(
									Decoration.node(pos, pos + node.nodeSize, {
										class: "pl-slot-empty",
										"data-placeholder": hint,
									}),
								)
							}
							return true
						})
						return DecorationSet.create(state.doc, decorations)
					},
				},
			}),
		]
	},
})
